import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";

import { Client } from "@twilio/conversations";
import { AttachIcon } from "@twilio-paste/icons/esm/AttachIcon";
import { Box, Button } from "@twilio-paste/core";
import { useTheme } from "@twilio-paste/theme";
import { Text } from "@twilio-paste/text";
import { Modal, ModalHeader, ModalHeading } from "@twilio-paste/modal";

import { ReduxMessage } from "../../../store/reducers/messageListReducer";
import { ReduxConversation } from "../../../store/reducers/convoReducer";
import {
  getTypingMessage,
  successNotification,
  unexpectedErrorNotification,
} from "../../../helpers";
import { actionCreators, AppState } from "../../../store";
import { getSdkConversationObject } from "../../../conversations-objects";
import { MAX_FILE_SIZE } from "../../../constants";

import NewMessageInput from "./NewMessageInput";
import SendMessageButton from "../../message/SendMessageButton";

import type { TemplateDto } from "../../../dto/template";
import {
  fetchTemplates,
  sendTemplateToConversation,
} from "../../../store/action-creators";
import { TemplateOnlyComposer } from "./TemplateOnlyComposer";

interface SendMessageProps {
  convoSid: string; // Twilio SID
  id: string; // Backend Conversation ID
  client: Client;
  messages: ReduxMessage[];
  convo: ReduxConversation;
  typingData: string[];
  droppedFiles: File[];
  templateOnly: boolean;
  inputDisabled: boolean;

  draftValue: string;
  onDraftChange: (value: string) => void;
}

const NewMessageInputField: React.FC<SendMessageProps> = (props) => {
  const [files, setFiles] = useState<File[]>([]);
  const [filesInputKey, setFilesInputKey] = useState<string>("input-key");

  const [isTemplateModalOpen, setTemplateModalOpen] = useState(false);

  const theme = useTheme();
  const typingInfo = getTypingMessage(props.typingData);

  const dispatch = useDispatch();
  const { addNotifications } = bindActionCreators(actionCreators, dispatch);

  const templates = useSelector((s: AppState) => s.templates) as TemplateDto[];
  const operator = useSelector((s: AppState) => s.operator);

  const pendingBySid =
    (useSelector((s: AppState) => (s as any).templateSendPendingBySid) as
      | Record<string, boolean>
      | undefined) ?? {};

  const isTemplatePending = pendingBySid[props.convoSid];

  const activeTemplates = useMemo(
    () =>
      templates.filter(
        (t) =>
          t.isActive &&
          (t.teams.length === 0 ||
            t.teams.some((team) => team.id === operator?.teamId))
      ),
    [templates, operator?.teamId]
  );

  const hasTemplates = activeTemplates.length > 0;

  const sdkConvo = useMemo(
    () => getSdkConversationObject(props.convo),
    [props.convo.sid]
  );

  useEffect(() => {
    setFiles([]);
    setFilesInputKey(Date.now().toString());
  }, [props.convoSid]);

  useEffect(() => {
    if (!files.length) {
      setFilesInputKey(Date.now().toString());
    }
  }, [files.length]);

  useEffect(() => {
    setFiles(props.droppedFiles);
  }, [props.droppedFiles]);

  useEffect(() => {
    if (!props.templateOnly) return;
    if (templates?.length) return;

    (dispatch as any)(fetchTemplates()).catch(() => {
      // opzionale: notification error
    });
  }, [props.templateOnly, templates?.length, dispatch]);

  const onFilesChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { files: assets } = event.target;
    if (!assets?.length) return;

    const validFiles = Array.from(assets).filter(
      ({ size }) => size < MAX_FILE_SIZE + 1
    );

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const onFileRemove = (file: string) => {
    const fileIdentityArray = file.split("_");
    const fileSize = Number(fileIdentityArray[fileIdentityArray.length - 1]);

    const fileName = fileIdentityArray
      .slice(0, fileIdentityArray.length - 1)
      .join("_");

    setFiles((prev) =>
      prev.filter(({ name, size }) => !(name === fileName && size === fileSize))
    );
  };

  const message = props.draftValue;
  const trimmedMessage = message.trim();

  const canSend = trimmedMessage.length > 0 || files.length > 0;

  const onMessageSend = async () => {
    if (!canSend) return;

    const currentConvoSid = props.convoSid;
    const currentSdkConvo = getSdkConversationObject(props.convo);

    if (!currentSdkConvo || currentSdkConvo.sid !== currentConvoSid) {
      unexpectedErrorNotification(
        "La conversazione non è ancora pronta. Riprova tra un attimo.",
        addNotifications
      );
      return;
    }

    const messageToSend = message;
    const filesToSend = [...files];

    try {
      const newMessageBuilder = currentSdkConvo
        .prepareMessage()
        .setBody(messageToSend);

      for (const file of filesToSend) {
        const fileData = new FormData();
        fileData.set(file.name, file, file.name);
        newMessageBuilder.addMedia(fileData);
      }

      const messageIndex = await newMessageBuilder.build().send();

      props.onDraftChange("");
      setFiles([]);

      await currentSdkConvo.advanceLastReadMessageIndex(messageIndex ?? 0);
    } catch (e: any) {
      unexpectedErrorNotification(
        e?.message ?? "Errore durante l'invio del messaggio",
        addNotifications
      );
    }
  };

  const onSendTemplate = async (
    tpl: TemplateDto,
    variables: Record<string, string>
  ) => {
    if (isTemplatePending) return;

    try {
      await (dispatch as any)(
        (sendTemplateToConversation as any)(
          props.convoSid,
          props.id,
          tpl.id,
          variables
        )
      );

      successNotification({
        message: "Invio template in corso…",
        addNotifications,
      });
    } catch (e: any) {
      unexpectedErrorNotification(
        e?.message ?? "Errore invio template",
        addNotifications
      );
    }
  };

  if (props.inputDisabled) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="space50"
        borderTopStyle="solid"
        borderTopWidth="borderWidth10"
        style={{ borderTopColor: theme.borderColors.colorBorderWeak }}
      >
        <Text as="p" color="colorTextWeak">
          Conversazione chiusa
        </Text>
      </Box>
    );
  }

  if (props.templateOnly) {
    return (
      <Box flex="1 1 auto" minWidth={0} width="100%" maxWidth="100%">
        <TemplateOnlyComposer
          templates={activeTemplates}
          onSend={onSendTemplate}
          disabled={isTemplatePending}
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        display="flex"
        flexBasis="60px"
        flexGrow={10}
        flexDirection="column"
        width="100%"
        maxWidth="100%"
        minWidth={0}
        borderTopStyle="solid"
        borderTopWidth="borderWidth10"
        style={{
          borderTopColor: theme.borderColors.colorBorderWeak,
          backgroundColor: theme.backgroundColors.colorBackgroundBody,
        }}
      >
        <Box
          paddingBottom="space20"
          paddingTop="space50"
          paddingLeft="space150"
          hidden={!props.typingData.length}
        >
          <Text as="p" color="colorTextIcon">
            {typingInfo}
          </Text>
        </Box>

        <Box
          display="flex"
          flexDirection="row"
          height="100%"
          flexGrow={10}
          width="100%"
          maxWidth="100%"
          minWidth={0}
          paddingBottom="space30"
          paddingTop="space40"
        >
          <Box
            paddingBottom="space30"
            paddingLeft="space50"
            paddingRight="space10"
            paddingTop="space20"
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
            alignItems="start"
            flex="0 0 auto"
          >
            <Button variant="link">
              <label htmlFor={`file-input-${props.convoSid}`}>
                <AttachIcon decorative title="Attach file" size="sizeIcon50" />
              </label>

              <input
                id={`file-input-${props.convoSid}`}
                key={filesInputKey}
                type="file"
                style={{ display: "none" }}
                onChange={onFilesChange}
              />
            </Button>
          </Box>

          <Box
            paddingRight="space50"
            flexGrow={10}
            minWidth={0}
            width="100%"
            maxWidth="100%"
          >
            <NewMessageInput
              assets={files}
              message={message}
              onChange={(value: string) => {
                if (sdkConvo?.sid === props.convoSid) {
                  sdkConvo.typing();
                }

                props.onDraftChange(value);
              }}
              onEnterKeyPress={async () => {
                await onMessageSend();
              }}
              onFileRemove={onFileRemove}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="start"
            flex="0 0 auto"
            columnGap="space10"
            paddingRight="space50"
          >
            <SendMessageButton message={message} onClick={onMessageSend} />

            <Button
              variant="secondary"
              onClick={() => setTemplateModalOpen(true)}
              disabled={isTemplatePending || !hasTemplates}
            >
              Seleziona template
            </Button>
          </Box>
        </Box>
      </Box>

      <Modal
        ariaLabelledby="template-composer-heading"
        isOpen={isTemplateModalOpen}
        onDismiss={() => setTemplateModalOpen(false)}
        size="default"
      >
        <ModalHeader>
          <ModalHeading as="h3" id="template-composer-heading">
            Seleziona un template
          </ModalHeading>
        </ModalHeader>

        <TemplateOnlyComposer
          templates={activeTemplates}
          onSend={async (tpl, vars) => {
            await onSendTemplate(tpl, vars);
            setTemplateModalOpen(false);
          }}
          disabled={isTemplatePending}
        />
      </Modal>
    </>
  );
};

export default NewMessageInputField;
